// ─────────────────────────────────────────────────────────────────────────────
// Known TABLE-typed datapoint IDs that benefit from a specific "Add row"
// label in the preview ("Add source", "Add waste material", etc.) instead of
// the generic fallback. Matches the old frontend's
// `QUESTIONNAIRE_TABLE_TYPE_MAPPING` so the responder sees the same wording
// they always have.
// ─────────────────────────────────────────────────────────────────────────────

export const SPECIALISED_TABLE_DATAPOINTS = {
  WASTE_WATER_TREATMENT_PROFICIENCY: 'waste_water_treatment_proficiency',
  WASTE_SOURCES: 'waste_sources',
  GHG_EMISSIONS_OFFSET_SCOPE: 'ghg_emissions_offset_scope',
  CARBON_INTENSITY_SCOPE: 'carbon_intensity_scope',
  ENERGY_SOURCES_PEFCR: 'energy_sources_pefcr',
  WATER_SOURCES: 'water_sources',
  NATIONALITIES: 'nationalities',
} as const;

export type SpecialisedDatapointId =
  (typeof SPECIALISED_TABLE_DATAPOINTS)[keyof typeof SPECIALISED_TABLE_DATAPOINTS];

/** Datapoints whose schema only ever has a single row — the add button
 *  must disappear once the user adds it. (GHG / Carbon expose 3 scope
 *  columns in a single row; there is no second row to add.) */
const SINGLE_ROW_DATAPOINTS = new Set<string>([
  SPECIALISED_TABLE_DATAPOINTS.GHG_EMISSIONS_OFFSET_SCOPE,
  SPECIALISED_TABLE_DATAPOINTS.CARBON_INTENSITY_SCOPE,
]);

export function isSingleRowDatapoint(datapointId: string | undefined): boolean {
  if (!datapointId) return false;
  return SINGLE_ROW_DATAPOINTS.has(datapointId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Curated unit lists for TABLE-typed datapoints whose NUMBER_DYNAMIC_UOM
// columns must NOT show the full global UOM list — the responder should only
// see units that make sense for the quantity being measured. Mirrors the
// legacy frontend's per-datapoint `Units` constants (`wasteUnits`,
// `energySourcesPefcrUnits`, etc.).
// ─────────────────────────────────────────────────────────────────────────────

interface SpecialisedUnit {
  /** Internal key — what's stored on the row. */
  value: string;
  /** Label shown in the dropdown — typically the short symbol or full name. */
  label: string;
}

const WASTE_SOURCE_UNITS: SpecialisedUnit[] = [
  { value: 'KILOGRAM', label: 'kg' },
  { value: 'METRIC_TON', label: 'metric ton' },
];

const ENERGY_SOURCE_UNITS: SpecialisedUnit[] = [
  { value: 'CUBIC_METRE', label: 'm³' },
  { value: 'LITRE', label: 'Litre' },
  { value: 'KILOWATT_HOUR', label: 'kWh' },
];

/** Returns the curated unit list for a TABLE-typed datapoint, or `null`
 *  when the datapoint has no override (cell editor falls back to the full
 *  UOM / CURRENCY list from `optionsForAsset`). */
export function unitsForDatapoint(
  datapointId: string | undefined,
): SpecialisedUnit[] | null {
  if (!datapointId) return null;
  switch (datapointId) {
    case SPECIALISED_TABLE_DATAPOINTS.WASTE_SOURCES:
      return WASTE_SOURCE_UNITS;
    case SPECIALISED_TABLE_DATAPOINTS.ENERGY_SOURCES_PEFCR:
      return ENERGY_SOURCE_UNITS;
    default:
      return null;
  }
}

/** Resolves the translation key for the per-datapoint "Add row" button.
 *  Returns `null` when the datapoint isn't one we have a specific label for —
 *  callers fall back to the generic `preview.field.tableAddRow`. */
export function addRowLabelKey(datapointId: string | undefined): string | null {
  switch (datapointId) {
    case SPECIALISED_TABLE_DATAPOINTS.ENERGY_SOURCES_PEFCR:
      return 'preview.field.addRow.energySource';
    case SPECIALISED_TABLE_DATAPOINTS.WATER_SOURCES:
      return 'preview.field.addRow.waterSource';
    case SPECIALISED_TABLE_DATAPOINTS.WASTE_WATER_TREATMENT_PROFICIENCY:
      return 'preview.field.addRow.wasteWaterTreatment';
    case SPECIALISED_TABLE_DATAPOINTS.WASTE_SOURCES:
      return 'preview.field.addRow.wasteMaterial';
    case SPECIALISED_TABLE_DATAPOINTS.GHG_EMISSIONS_OFFSET_SCOPE:
      return 'preview.field.addRow.ghgScope';
    case SPECIALISED_TABLE_DATAPOINTS.CARBON_INTENSITY_SCOPE:
      return 'preview.field.addRow.carbonScope';
    case SPECIALISED_TABLE_DATAPOINTS.NATIONALITIES:
      return 'preview.field.addRow.nationality';
    default:
      return null;
  }
}
